/**
 * Starts background AI plan jobs after trip create/import — mirrors iOS
 * AIPlanJobService + AIPlanJobQueueService.
 *
 * Enqueues eligible city visits, POSTs /plan serially, and persists `aiJobId`
 * (plus status timestamps) on each city visit via tripsService / Device Sync.
 */

import { isAtSeaLocation } from './cruiseItinerariesApi';
import { createPlanJob } from './planningApi';
import { includesAIPlan } from './subscription/subscriptionService';
import { getTrip, getUserIdForTripWrites, updateTrip } from './tripsService';
import { loadLocalUserPreferences } from './userPreferencesSyncService';
import type { Trip, TripCityVisitMeta } from '../types/client';

const QUEUE_STORAGE_KEY = 'wt_ai_plan_job_queue_v1';
const FAILURE_BACKOFF_SECONDS = [5, 15, 45, 120, 300];
const NORMAL_FAILURE_LIMIT = 5;

export interface StartPlanJobsOptions {
  /** When true, clear existing aiJobId before re-enqueue. */
  clearExistingPlanData?: boolean;
  /** When set, only enqueue these city visit ids (iOS filtered list). */
  cityVisitIds?: string[];
}

interface QueueItem {
  cityVisitId: string;
  tripId: string;
  clearExistingPlanData: boolean;
  enqueuedAt: string;
  failureCount: number;
}

interface QueueState {
  items: QueueItem[];
  pausedUntil: string | null;
  backendUnavailableMode: boolean;
}

let workerRunning = false;

function emptyState(): QueueState {
  return { items: [], pausedUntil: null, backendUnavailableMode: false };
}

function loadQueue(): QueueState {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<QueueState>;
    return {
      items: Array.isArray(parsed.items) ? (parsed.items as QueueItem[]) : [],
      pausedUntil: typeof parsed.pausedUntil === 'string' ? parsed.pausedUntil : null,
      backendUnavailableMode: Boolean(parsed.backendUnavailableMode),
    };
  } catch {
    return emptyState();
  }
}

function saveQueue(state: QueueState): void {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

/** Mirrors iOS iso8601LocalString used on POST /plan cityVisit dates. */
function iso8601LocalFromYmd(ymd: string | null, hm: string | null): string {
  const date = ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : new Date().toISOString().slice(0, 10);
  const time = hm && /^\d{2}:\d{2}$/.test(hm) ? `${hm}:00` : '00:00:00';
  return `${date}T${time}Z`;
}

function failurePauseSeconds(failureCount: number): number {
  const index = Math.min(Math.max(failureCount - 1, 0), FAILURE_BACKOFF_SECONDS.length - 1);
  return FAILURE_BACKOFF_SECONDS[index];
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function persistAiJobOnCityVisit(
  tripId: string,
  cityVisitId: string,
  planId: string,
  clearExistingPlanData: boolean
): boolean {
  const userId = getUserIdForTripWrites();
  if (!userId) {
    console.error('AIPlanJobService: cannot save planId — no user id');
    return false;
  }
  const trip = getTrip(userId, tripId);
  if (!trip) {
    console.error(`AIPlanJobService: cannot save planId — trip ${tripId} not found`);
    return false;
  }

  const now = new Date().toISOString();
  const prev = trip.cityVisitMeta?.[cityVisitId] ?? {
    arrivalDate: null,
    departureDate: null,
    portArrivalHm: null,
    portDepartureHm: null,
    cruisePortStatus: null,
  };
  const nextMeta: TripCityVisitMeta = {
    ...prev,
    aiJobId: planId,
    aiJobStartedAt: now,
    aiStatus: 'queued',
    aiUpdatedAt: now,
  };

  const cityVisitMeta = { ...(trip.cityVisitMeta ?? {}), [cityVisitId]: nextMeta };
  const updated = updateTrip(userId, tripId, { cityVisitMeta });
  if (!updated) return false;

  if (clearExistingPlanData) {
    // Web does not yet store aiGeneratedPlanData / cityDescription locally.
  }
  return true;
}

async function kickoffCityVisit(
  trip: Trip,
  cityVisitId: string,
  clearExistingPlanData: boolean
): Promise<{ ok: true; planId: string } | { ok: false; reason: string; retriable: boolean }> {
  const cityName = trip.cityNames?.[cityVisitId] || cityVisitId;
  if (isAtSeaLocation(cityName)) {
    return { ok: false, reason: 'at-sea', retriable: false };
  }

  const meta = trip.cityVisitMeta?.[cityVisitId];
  const existing = meta?.aiJobId?.trim();
  if (!clearExistingPlanData && existing) {
    return { ok: false, reason: `already has aiJobId=${existing}`, retriable: false };
  }

  const coords = trip.cityCoords?.[cityVisitId];
  const lat = coords?.latitude ?? 0;
  const lon = coords?.longitude ?? 0;
  if (lat === 0 && lon === 0) {
    console.error(`AIPlanJobService: missing coordinates for ${cityName}`);
    return { ok: false, reason: 'missing-coordinates', retriable: false };
  }

  if (!navigator.onLine) {
    return { ok: false, reason: 'offline', retriable: true };
  }

  const prefs = loadLocalUserPreferences().travelerPreferenceProfile;
  const body = {
    cityVisit: {
      id: cityVisitId,
      cityName: cityName.slice(0, 100),
      latitude: lat,
      longitude: lon,
      startDate: iso8601LocalFromYmd(meta?.arrivalDate ?? null, meta?.portArrivalHm ?? null),
      endDate: iso8601LocalFromYmd(meta?.departureDate ?? meta?.arrivalDate ?? null, meta?.portDepartureHm ?? null),
    },
    travelerPreferences: (prefs.additionalPreferences ?? '').trim(),
    preferredActivities: prefs.preferredActivities ?? [],
    sightseeingStyle: prefs.sightseeingStyle ?? undefined,
  };

  console.info(`AIPlanJobService: POST /plan for ${cityName}`, body.cityVisit);

  const result = await createPlanJob(body);
  if (!result.ok || !result.planId) {
    return {
      ok: false,
      reason: result.errorMessage || `HTTP ${result.status}`,
      retriable: true,
    };
  }

  const saved = persistAiJobOnCityVisit(trip.id, cityVisitId, result.planId, clearExistingPlanData);
  if (!saved) {
    return { ok: false, reason: 'save-failed', retriable: true };
  }

  console.info(`AIPlanJobService: plan started for ${cityName} id=${result.planId}`);
  return { ok: true, planId: result.planId };
}

async function processQueue(): Promise<void> {
  if (workerRunning) return;
  workerRunning = true;
  try {
    while (true) {
      if (!includesAIPlan()) {
        console.info('AIPlanJobQueue: stopped — subscription does not include AI planning');
        break;
      }

      let state = loadQueue();
      if (state.items.length === 0) break;

      if (state.pausedUntil) {
        const pauseMs = new Date(state.pausedUntil).getTime() - Date.now();
        if (pauseMs > 0) {
          console.info(`AIPlanJobQueue: sleeping ${Math.round(pauseMs / 1000)}s before retry`);
          await sleep(pauseMs);
        }
        state = loadQueue();
        state.pausedUntil = null;
        saveQueue(state);
      }

      state = loadQueue();
      const head = state.items[0];
      if (!head) break;

      const userId = getUserIdForTripWrites();
      if (!userId) {
        console.warn('AIPlanJobQueue: no user id — pausing queue');
        await sleep(5000);
        continue;
      }

      const trip = getTrip(userId, head.tripId);
      if (!trip) {
        console.warn(`AIPlanJobQueue: dropping — trip ${head.tripId} not found`);
        state.items = state.items.filter((i) => i.cityVisitId !== head.cityVisitId);
        saveQueue(state);
        continue;
      }

      const cityName = trip.cityNames?.[head.cityVisitId] || head.cityVisitId;
      console.info(
        `AIPlanJobQueue: processing ${cityName} (failures=${head.failureCount}/${NORMAL_FAILURE_LIMIT})`
      );

      const result = await kickoffCityVisit(trip, head.cityVisitId, head.clearExistingPlanData);

      state = loadQueue();
      const headIndex = state.items.findIndex((i) => i.cityVisitId === head.cityVisitId);
      if (headIndex < 0) continue;

      if (result.ok) {
        state.items.splice(headIndex, 1);
        state.pausedUntil = null;
        state.backendUnavailableMode = false;
        saveQueue(state);
        continue;
      }

      if (!result.retriable || result.reason === 'offline') {
        if (result.reason === 'offline') {
          console.info(`AIPlanJobQueue: offline — retaining ${cityName}`);
          await sleep(5000);
          continue;
        }
        console.warn(`AIPlanJobQueue: dropping ${cityName} — ${result.reason}`);
        state.items.splice(headIndex, 1);
        saveQueue(state);
        continue;
      }

      const item = { ...state.items[headIndex], failureCount: state.items[headIndex].failureCount + 1 };
      if (item.failureCount >= NORMAL_FAILURE_LIMIT) {
        console.warn(`AIPlanJobQueue: dropping ${cityName} after ${item.failureCount} failures: ${result.reason}`);
        state.items.splice(headIndex, 1);
        state.pausedUntil = null;
        state.backendUnavailableMode = true;
        saveQueue(state);
        continue;
      }

      const pause = failurePauseSeconds(item.failureCount);
      state.items[headIndex] = item;
      state.pausedUntil = new Date(Date.now() + pause * 1000).toISOString();
      saveQueue(state);
      console.warn(
        `AIPlanJobQueue: failed for ${cityName} (attempt ${item.failureCount}/${NORMAL_FAILURE_LIMIT}): ${result.reason} — pausing ${pause}s`
      );
      await sleep(pause * 1000);
    }
  } finally {
    workerRunning = false;
  }
}

function startWorkerIfNeeded(): void {
  if (workerRunning) return;
  void processQueue();
}

/**
 * Enqueue AI plan kickoffs for eligible cities on a trip.
 * No-ops when the subscription does not include AI planning.
 */
export function startPlanJobs(trip: Trip, options: StartPlanJobsOptions = {}): boolean {
  if (!includesAIPlan()) {
    console.info('AIPlanJobService: skipped — subscription does not include AI planning');
    return false;
  }

  const filter = options.cityVisitIds;
  const cityIds =
    filter && filter.length > 0
      ? filter
      : trip.cityIds ?? [];
  if (cityIds.length === 0) return false;

  const clearExistingPlanData = Boolean(options.clearExistingPlanData);
  const state = loadQueue();
  const existingIds = new Set(state.items.map((i) => i.cityVisitId));
  let enqueued = 0;

  for (const cityVisitId of cityIds) {
    const cityName = trip.cityNames?.[cityVisitId] || '';
    if (isAtSeaLocation(cityName)) continue;

    if (existingIds.has(cityVisitId)) {
      if (clearExistingPlanData) {
        const index = state.items.findIndex((i) => i.cityVisitId === cityVisitId);
        if (index >= 0) {
          state.items[index] = {
            cityVisitId,
            tripId: trip.id,
            clearExistingPlanData: true,
            enqueuedAt: new Date().toISOString(),
            failureCount: 0,
          };
          enqueued += 1;
        }
      }
      continue;
    }

    state.items.push({
      cityVisitId,
      tripId: trip.id,
      clearExistingPlanData,
      enqueuedAt: new Date().toISOString(),
      failureCount: 0,
    });
    existingIds.add(cityVisitId);
    enqueued += 1;
  }

  saveQueue(state);
  console.info(
    `AIPlanJobService: enqueued ${enqueued} city/cities for trip ${trip.title || trip.id} (queue=${state.items.length})`
  );
  startWorkerIfNeeded();
  return enqueued > 0 || state.items.length > 0;
}

/** Resume pending queue after login / app reload — mirrors iOS resumeIfNeeded. */
export function resumePlanJobsIfNeeded(): void {
  if (!includesAIPlan()) return;
  const state = loadQueue();
  if (state.items.length === 0) return;
  console.info(`AIPlanJobQueue: resume — ${state.items.length} pending`);
  startWorkerIfNeeded();
}
